import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";


import { handleVerifyTransaction } from "../../shared/controllers/transactionController";
import transactionReconfirmationService from "../../utils/transactionReconfirmation";

import green from "./assets/green.svg";

const getErrorMessage = (error: unknown): string => {
    if (error instanceof Error && error.message) {
        return error.message;
    }

    if (
        typeof error === "object" &&
        error !== null &&
        typeof (error as { message?: unknown }).message === "string"
    ) {
        return (error as { message: string }).message;
    }

    return "There was an error processing your payment. Please try again.";
};

const PaymentVerify: React.FC = () => {
    const [isChecking, setIsChecking] = useState(true); // State to control the loading state
    const [isSuccess, setIsSuccess] = useState(false); // State to determine if the transaction was successful
    const [isPending, setIsPending] = useState(false);
    const [errorMessage, setErrorMessage] = useState(""); // State for error message
    const location = useLocation(); // Used to extract the URL params

    // Extract payment_intent and payment_intent_client_secret from the URL
    const queryParams = new URLSearchParams(location.search);
    const paymentIntent = queryParams.get("payment_intent");
    const paymentIntentClientSecret = queryParams.get(
        "payment_intent_client_secret"
    );
    const paymentMethod = queryParams.get("payment_method");
    const transactionId = queryParams.get("transaction_id");

    useEffect(() => {
        let cancelled = false;

        if (paymentIntent && paymentIntentClientSecret) {
            const userData = {
                payment_intent_id: paymentIntent,
                payment_method: paymentMethod,
            };

            const verify = async () => {
                try {
                    if (transactionId) {
                        const result = await transactionReconfirmationService.handleStripeSuccess(
                            transactionId,
                            { id: paymentIntent }
                        );

                        if (result.success) {
                            if (!cancelled) setIsSuccess(true);
                        } else if (result.pending) {
                            const pollResult = await transactionReconfirmationService.pollTransactionStatus(
                                transactionId,
                                { maxAttempts: 15, interval: 2000 }
                            );

                            if (pollResult.success) {
                                if (!cancelled) setIsSuccess(true);
                            } else if (!cancelled) {
                                setIsPending(true);
                                setErrorMessage(
                                    result.requiresAction
                                        ? "Bank verification was not completed. Return to your booking and try again."
                                        : "Your bank is still processing this payment. Your booking will update automatically once it is confirmed."
                                );
                            }
                        } else if (!cancelled) {
                            setErrorMessage(result.error || "This payment could not be verified.");
                        }
                    } else {
                        await handleVerifyTransaction(userData);
                        if (!cancelled) setIsSuccess(true);
                    }
                } catch (error: unknown) {
                    if (!cancelled) setErrorMessage(getErrorMessage(error));
                } finally {
                    if (!cancelled) setIsChecking(false);
                }
            };
            verify();
        } else {
            // If the payment details are missing, set an error message
            setErrorMessage("Missing payment information. Please try again.");
            setIsChecking(false);
        }

        return () => {
            cancelled = true;
        };
    }, [paymentIntent, paymentIntentClientSecret, paymentMethod, transactionId]);

    return (
        <div className="w-full flex items-center justify-center">
            <div className="px-4 max-w-[600px] flex flex-col w-full justify-center h-svh items-center">
                {isChecking ? (
                    <>
                        <p className="text-xl font-Manrope font-semibold text-black mt-3 text-center">
                            Checking Transaction...
                        </p>
                        <p className="mt-3 max-w-[400px] text-center font-Manrope font-normal text-base text-black">
                            We're processing your payment, please wait a moment.
                        </p>
                    </>
                ) : isSuccess ? (
                    <>
                        <img src={green} alt="Success" />
                        <p className="text-xl font-Manrope font-semibold text-black mt-3 text-center">
                            Payment Received! You're All Set!
                        </p>
                        <p className="mt-3 max-w-[400px] text-center font-Manrope font-normal text-base text-black">
                            Your payment has been processed. Get ready for the fun to begin!
                        </p>
                        <Link
                            to="/dashboard"
                            className="bg-[#317333] rounded-md py-3 mt-4 px-4 font-Manrope text-[#fff] font-semibold text-base flex justify-center items-center"
                        >
                            Return to Dashboard
                        </Link>
                    </>
                ) : (
                    <>
                        <p className="text-xl font-Manrope font-semibold text-black mt-3 text-center">
                            {isPending ? "Payment Pending" : "Payment Not Completed"}
                        </p>
                        <p className="mt-3 max-w-[400px] text-center font-Manrope font-normal text-base text-black">
                            {errorMessage}
                        </p>
                        <Link
                            to="/dashboard"
                            className={`${isPending ? "bg-[#317333]" : "bg-[#D9534F]"} rounded-md py-3 mt-4 px-4 font-Manrope text-[#fff] font-semibold text-base flex justify-center items-center`}
                        >
                            Return to Dashboard
                        </Link>
                    </>
                )}
            </div>
        </div>
    );
};

export default PaymentVerify;
